import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { FaFileInvoiceDollar } from "react-icons/fa";
import "react-toastify/dist/ReactToastify.css";

import useScrollToTop from "../../../../hooks/useScollToTop";
import { useUser } from "../../../../pages/Home/UserContext";

import { useNotification } from "./../../../../components/Notification/NotificationManager";

import {
  approveExpenseClaim,
  createExpenseItem,
  deleteExpenseClaim,
  deleteExpenseItem,
  exportExpenseClaims,
  getBudgetCodes,
  getExpenseClaimById,
  getExpenseClaims,
  markExpenseClaimPaid,
  rejectExpenseClaim,
  updateExpenseClaim,
  updateExpenseItem,
} from "../../../../services/expenseServices";
import { getEventDetails } from "../../../../services/eventServices";

import ExpenseFilters from "./components/ExpenseFilters";
import ExpenseStatsCards from "./components/ExpenseStatsCards";
import ExpenseClaimsTable from "./components/ExpenseClaimsTable";
import ExpenseClaimDetailsDrawer from "./components/ExpenseClaimDetailsDrawer";

import {
  cleanFilters,
  downloadBlob,
  sortClaims,
} from "./utils/expenseAdminFormat";

const EMPTY_FILTERS = {
  q: "",
  status: "",
  eventId: "",
  budgetCodeId: "",
  from: "",
  to: "",
};

export default function ExpensePageAdmin() {
  useScrollToTop();

  const { user } = useUser();

  const currentUser = useMemo(() => {
    if (user) return user;
    try {
      return JSON.parse(localStorage.getItem("userInfo") || "null");
    } catch {
      return null;
    }
  }, [user]);

  const validatorUserId = currentUser?.userId ?? currentUser?.id ?? null;

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState({});
  const [budgetCodes, setBudgetCodes] = useState([]);

  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState("");

  const [eventDetailsMap, setEventDetailsMap] = useState({});

  const loadBudgetCodes = useCallback(async () => {
    try {
      const data = await getBudgetCodes();
      setBudgetCodes(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
      toast.error("Impossible de charger les codes budgétaires.");
    }
  }, []);

  const loadEventDetailsForClaims = useCallback(async (rows = []) => {
    try {
      const ids = [
        ...new Set((rows || []).map((c) => c?.event_id).filter(Boolean)),
      ];

      if (!ids.length) return;

      const results = await Promise.allSettled(
        ids.map((id) => getEventDetails(id)),
      );

      setEventDetailsMap((prev) => {
        const next = { ...prev };
        results.forEach((result, index) => {
          const eventId = ids[index];
          if (result.status === "fulfilled" && result.value) {
            next[eventId] = result.value;
          }
        });
        return next;
      });
    } catch (error) {
      console.error(
        "Erreur lors du chargement des détails des événements:",
        error,
      );
    }
  }, []);

  const loadClaims = useCallback(
    async (params = appliedFilters) => {
      try {
        setLoading(true);
        const data = await getExpenseClaims(params);
        const rows = Array.isArray(data) ? data : [];
        setClaims(rows);
        await loadEventDetailsForClaims(rows);
      } catch (error) {
        console.error(error);
        toast.error("Impossible de charger les fiches de frais.");
      } finally {
        setLoading(false);
      }
    },
    [appliedFilters, loadEventDetailsForClaims],
  );

  const loadClaimDetails = useCallback(
    async (claimId) => {
      if (!claimId) return;
      try {
        setDetailLoading(true);
        const data = await getExpenseClaimById(claimId);
        setSelectedClaim(data);

        if (data?.event_id && !eventDetailsMap[data.event_id]) {
          try {
            const ev = await getEventDetails(data.event_id);
            setEventDetailsMap((prev) => ({
              ...prev,
              [data.event_id]: ev,
            }));
          } catch (err) {
            console.error("Erreur détail événement:", err);
          }
        }
      } catch (error) {
        console.error(error);
        toast.error("Impossible de charger le détail de la fiche.");
      } finally {
        setDetailLoading(false);
      }
    },
    [eventDetailsMap],
  );

  useEffect(() => {
    loadBudgetCodes();
  }, [loadBudgetCodes]);

  useEffect(() => {
    loadClaims(appliedFilters);
  }, [appliedFilters, loadClaims]);

  const sortedClaims = useMemo(
    () => sortClaims(claims, sortBy, sortDir),
    [claims, sortBy, sortDir],
  );

  const handleChangeFilter = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = (e) => {
    e?.preventDefault?.();
    setAppliedFilters(cleanFilters(filters));
  };

  const handleResetFilters = () => {
    setFilters(EMPTY_FILTERS);
    setAppliedFilters({});
  };

  const handleRefresh = () => {
    loadClaims(appliedFilters);
    if (selectedId) loadClaimDetails(selectedId);
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const blob = await exportExpenseClaims(appliedFilters);
      const d = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      const filename = `fiches_frais_admin_${d.getFullYear()}-${pad(
        d.getMonth() + 1,
      )}-${pad(d.getDate())}.csv`;
      downloadBlob(blob, filename);
      toast.success("Export généré.");
    } catch (error) {
      console.error(error);
      toast.error("Échec de l’export.");
    } finally {
      setExporting(false);
    }
  };

  const handleSort = (field) => {
    const target = field === "status" ? "status" : field;

    setSortBy((prev) => {
      if (prev === target) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        return prev;
      }
      setSortDir("asc");
      return target;
    });
  };

  const openClaim = async (claimOrId) => {
    const id =
      typeof claimOrId === "number" || typeof claimOrId === "string"
        ? Number(claimOrId)
        : claimOrId?.id;

    if (!id) return;

    setSelectedId(id);
    setDrawerOpen(true);
    await loadClaimDetails(id);
  };

  const refreshAfterWorkflow = async (claimId) => {
    await loadClaims(appliedFilters);
    await loadClaimDetails(claimId);
  };

  const handleApprove = async (adminCommentaire) => {
    if (!selectedClaim?.id) return;

    try {
      setActionLoading("approve");
      await approveExpenseClaim(selectedClaim.id, {
        validated_by_user_id: validatorUserId,
        admin_commentaire: adminCommentaire || null,
      });
      toast.success("Fiche approuvée.");
      await refreshAfterWorkflow(selectedClaim.id);
    } catch (error) {
      console.error(error);
      toast.error("Impossible d’approuver la fiche.");
    } finally {
      setActionLoading("");
    }
  };

  const handleReject = async (adminCommentaire) => {
    if (!selectedClaim?.id) return;

    try {
      setActionLoading("reject");
      await rejectExpenseClaim(selectedClaim.id, {
        validated_by_user_id: validatorUserId,
        admin_commentaire: adminCommentaire || null,
      });
      toast.success("Fiche rejetée.");
      await refreshAfterWorkflow(selectedClaim.id);
    } catch (error) {
      console.error(error);
      toast.error("Impossible de rejeter la fiche.");
    } finally {
      setActionLoading("");
    }
  };

  const handleSaveClaim = async (payload) => {
    if (!selectedClaim?.id) return;

    try {
      setActionLoading("save-claim");
      await updateExpenseClaim(selectedClaim.id, payload);
      toast.success("Fiche mise à jour.");
      await refreshAfterWorkflow(selectedClaim.id);
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.message ||
          "Impossible de modifier la fiche de frais.",
      );
    } finally {
      setActionLoading("");
    }
  };

  const handleCreateItem = async (payload) => {
    if (!selectedClaim?.id) return;

    try {
      setActionLoading("create-item");
      await createExpenseItem(selectedClaim.id, payload);
      toast.success("Ligne de frais ajoutée.");
      await refreshAfterWorkflow(selectedClaim.id);
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.message || "Impossible d’ajouter la ligne.",
      );
      throw error;
    } finally {
      setActionLoading("");
    }
  };

  const handleUpdateItem = async (itemId, payload) => {
    if (!itemId || !selectedClaim?.id) return;

    try {
      setActionLoading(`update-item-${itemId}`);
      await updateExpenseItem(itemId, payload);
      toast.success("Ligne de frais mise à jour.");
      await refreshAfterWorkflow(selectedClaim.id);
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.message || "Impossible de modifier la ligne.",
      );
      throw error;
    } finally {
      setActionLoading("");
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!itemId || !selectedClaim?.id) return;

    try {
      setActionLoading(`delete-item-${itemId}`);
      await deleteExpenseItem(itemId);
      toast.success("Ligne de frais supprimée.");
      await refreshAfterWorkflow(selectedClaim.id);
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.message || "Impossible de supprimer la ligne.",
      );
      throw error;
    } finally {
      setActionLoading("");
    }
  };

  const handleDeleteClaim = async () => {
    if (!selectedClaim?.id) return;

    if (["approved", "paid"].includes(selectedClaim.statut)) {
      toast.error(
        "Les fiches approuvées ou payées ne peuvent pas être supprimées.",
      );
      return;
    }

    const ok = window.confirm(
      `Confirmer la suppression de la fiche de frais #${selectedClaim.id} ?\n\nCette action est définitive et doit aussi supprimer les justificatifs rattachés côté back.`,
    );

    if (!ok) return;

    try {
      setActionLoading("delete-claim");

      await deleteExpenseClaim(selectedClaim.id);

      toast.success("Fiche de frais supprimée.");

      setDrawerOpen(false);
      setSelectedClaim(null);
      setSelectedId(null);

      await loadClaims(appliedFilters);
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.message ||
          "Impossible de supprimer la fiche de frais.",
      );
    } finally {
      setActionLoading("");
    }
  };

  const handleMarkPaid = async (adminCommentaire) => {
    if (!selectedClaim?.id) return;

    try {
      setActionLoading("paid");
      await markExpenseClaimPaid(selectedClaim.id, {
        validated_by_user_id: validatorUserId,
        admin_commentaire: adminCommentaire || null,
      });
      toast.success("Fiche marquée comme payée.");
      await refreshAfterWorkflow(selectedClaim.id);
    } catch (error) {
      console.error(error);
      toast.error("Impossible de passer la fiche en payée.");
    } finally {
      setActionLoading("");
    }
  };

  const eventOptions = useMemo(() => {
    const map = new Map();

    claims.forEach((claim) => {
      if (!claim?.event_id) return;

      const details = eventDetailsMap?.[claim.event_id];
      const label =
        details?.titre ||
        claim?.ClaimEvent?.titre ||
        claim?.ClaimEvent?.nom_event ||
        claim?.ClaimEvent?.nom ||
        claim?.ClaimEvent?.title ||
        `Événement ${claim.event_id}`;

      if (!map.has(claim.event_id)) {
        map.set(claim.event_id, {
          value: String(claim.event_id),
          label,
        });
      }
    });

    return Array.from(map.values()).sort((a, b) =>
      a.label.localeCompare(b.label, "fr"),
    );
  }, [claims, eventDetailsMap]);

  return (
    <div className="w-full pt-[84px] font-satoshi">
      <ToastContainer position="top-right" autoClose={3000} />

      <main className="px-4 py-6 sm:px-6 lg:px-8">
        <div className="rounded-[2rem] bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 px-6 py-7 text-white shadow-xl">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="inline-flex h-14 w-14 items-center justify-center rounded-3xl bg-white/10 ring-1 ring-white/15">
                <FaFileInvoiceDollar className="text-2xl" />
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-300">
                  Administration
                </div>
                <h1 className="mt-1 text-3xl font-black tracking-tight">
                  Gestion des fiches de frais
                </h1>
                <p className="mt-2 max-w-3xl text-sm text-slate-300">
                  Centralise les demandes, filtre les fiches, contrôle les
                  justificatifs, modifie les fiches en cours et pilote le
                  workflow de validation jusqu’au paiement.
                </p>
              </div>
            </div>

            <div className="rounded-3xl bg-white/10 px-5 py-4 text-right ring-1 ring-white/15">
              <div className="text-xs uppercase tracking-wide text-slate-300">
                Fiches affichées
              </div>
              <div className="mt-1 text-3xl font-black">
                {sortedClaims.length}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6">
          <ExpenseStatsCards claims={claims} />
        </div>

        <div className="mt-6">
          <ExpenseFilters
            filters={filters}
            onChange={handleChangeFilter}
            onSubmit={handleApplyFilters}
            onReset={handleResetFilters}
            onRefresh={handleRefresh}
            onExport={handleExport}
            budgetCodes={budgetCodes}
            eventOptions={eventOptions}
            loading={loading}
            exporting={exporting}
          />
        </div>

        <div className="mt-6">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div>
              <div className="text-xl font-black text-slate-900">
                Liste des fiches
              </div>
              <div className="text-sm text-slate-500">
                Clique sur une ligne pour afficher le détail complet.
              </div>
            </div>

            <div className="rounded-2xl bg-white px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm ring-1 ring-slate-200">
              Tri : {sortBy} · {sortDir}
            </div>
          </div>

          <ExpenseClaimsTable
            claims={sortedClaims}
            loading={loading}
            sortBy={sortBy}
            sortDir={sortDir}
            onSort={handleSort}
            onOpen={openClaim}
            selectedId={selectedId}
            eventDetailsMap={eventDetailsMap}
          />
        </div>
      </main>

      <ExpenseClaimDetailsDrawer
        open={drawerOpen}
        claim={selectedClaim}
        loading={detailLoading}
        onClose={() => setDrawerOpen(false)}
        onApprove={handleApprove}
        onReject={handleReject}
        onMarkPaid={handleMarkPaid}
        onDelete={handleDeleteClaim}
        onSaveClaim={handleSaveClaim}
        onCreateItem={handleCreateItem}
        onUpdateItem={handleUpdateItem}
        onDeleteItem={handleDeleteItem}
        actionLoading={actionLoading}
        eventDetailsMap={eventDetailsMap}
        budgetCodes={budgetCodes}
      />
    </div>
  );
}
